import * as React from 'react';
// import Cookies from 'js-cookie';
import { NextPage } from 'next';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import LazyLoad from 'react-lazyload';
// const validate = require('uuid-validate');
import { eero } from '@opendoor/bricks/theme';
import { ITheme } from '@opendoor/bricks/system/theme';

import * as api from '../components/api';
import { HERO_IMAGES } from '../components/globals';
import GlobalStyles from '../components/shared/GlobalStyles';
import StickyCTAStyles from '../components/shared/StickyCTAStyles';
import StickyCTABar from '../components/shared/StickyCTABar';
import MenuPanel, { MenuOverlay } from '../components/shared/MenuPanel';

import Header from '../components/shared/Header';
import Hero from '../components/homepage-2019/Hero';
import Press from '../components/shared/Press';
import HowItWorks from '../components/homepage-2019/HowItWorks';

// We dynamically load the below sections once the user scrolls down a bit
// Bundle them all in one chunk to save on compression overhead
const SocialProofOdometer = dynamic(
  () =>
    import(
      /* webpackChunkName: "BelowFoldSections" */ '../components/homepage-2019/SocialProofOdometer'
    ),
);
const Testimonials = dynamic(
  () =>
    import(/* webpackChunkName: "BelowFoldSections" */ '../components/homepage-2019/Testimonials'),
);
const SellingOptions = dynamic(
  () =>
    import(
      /* webpackChunkName: "BelowFoldSections" */ '../components/homepage-2019/SellingOptions'
    ),
);
const BuyerPanel = dynamic(
  () =>
    import(/* webpackChunkName: "BelowFoldSections" */ '../components/homepage-2019/BuyerPanel'),
);
const FAQs = dynamic(
  () => import(/* webpackChunkName: "BelowFoldSections" */ '../components/homepage-2019/FAQs'),
);
const Contact = dynamic(
  () => import(/* webpackChunkName: "BelowFoldSections" */ '../components/shared/Contact'),
);

const AppBanner = dynamic(
  () => import(/* webpackChunkName: "BelowFoldSections" */ '../components/shared/AppBanner'),
);
const Footer = dynamic(
  () => import(/* webpackChunkName: "BelowFoldSections" */ '../components/shared/Footer'),
);

// we use these to build a placeholder for lazy-loaded sections, in case of slow loading
import { Flex, Loader } from '@opendoor/bricks/core';

import * as WordpressHeadGlobal from '../components/WordpressPorts';
import { useObservability } from '@opendoor/observability';
import { ParsedUrlQuery } from 'querystring';
import { HOMEPAGE_SCHEMA } from '../schema';
import ReturnExperienceBanner, {
  ExperienceType,
  OFFER_EXPIRY_DAYS_CUTOFF,
} from '../components/shared/ReturnExperienceBanner';
import imgixUrl from '../helpers/imgixUrl';
import UnsupportedBrowserBanner, {
  isUnsupportedBrowser,
} from '../components/shared/UnsupportedBrowserBanner';
import { useAuth } from '@opendoor/auth-fe';
import { useOnLoad } from '../components/shared/useOnLoad';

export interface ReturnExperienceData {
  address?: string;
  addressToken?: string;
  customerUUID?: string;
  firstName?: string;
  offerRequestSource?: string;
  currentOfferHeadlinePriceCents?: number;
  previousOfferHeadlinePriceCents?: number;
  offerExpiresInDays?: number;
  sellerInputUUID?: string;
  experienceType: ExperienceType | null;
}

interface ServerSideProps {
  region: string | null;
  markets: [];
}
const Homepage2019: React.FC<ServerSideProps> & { theme: ITheme } = (props) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [stickyVisible, setStickyVisible] = React.useState(false);
  const [returnExperienceData, setReturnExperienceData] = React.useState<ReturnExperienceData>({
    experienceType: null,
  });
  const returnExperienceBannerShown = returnExperienceData.experienceType !== null;
  const { trackEvent } = useObservability();
  const { user, authentication, refresh } = useAuth();
  const isLoggedIn = authentication.state === 'authenticated';
  React.useEffect(() => {
    try {
      // TODO: Make trackEvent handle initObservable not loaded better
      // TODO: Make cosmos handle uncaught exceptions better - currently we
      // just keep trying to rerender the same code until we don't get an
      // exception
      trackEvent(
        'viewed',
        'new_homepage',
        {
          'homepage-2019-test': 'homepage-2019-3',
          logged_in: false,
        },
        { categoryOverride: 'homepage' },
      );
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.error('Unable to homepage view event', e);
    }
  }, [trackEvent]);
  React.useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    if (!user || !user.customerUuid) {
      return;
    }
    const customerUUID = user.customerUuid;
    // trackEvent('viewed', 'logged_in', undefined, { categoryOverride: 'homepage' });
    async function start() {
      const result = await fetchSellerReturnExperience(customerUUID);
      if (!result) {
        return;
      }
      setReturnExperienceData(result.returnExperienceData);
    }
    start();
  }, [isLoggedIn, user]);

  const isAfterLoad = useOnLoad();
  React.useEffect(() => {
    // don't fire off the import until the document is finished loading
    if (authentication.state !== 'not_authenticated' || !isAfterLoad) {
      return;
    }
    import('../components/shared/GoogleOneTap').then(({ promptGoogleOneTap }) => {
      promptGoogleOneTap('cosmos-homepage', refresh);
    });
  }, [authentication.state, isAfterLoad, refresh]);
  const menuTrigger = React.useRef<Element>();
  const toggleMenu = () => {
    setIsMenuOpen((isOpen) => {
      // If opening the menu, store which focused element triggered the action (if any)
      if (!isOpen && document.activeElement) {
        menuTrigger.current = document.activeElement;
      }
      // If closing the menu, restore focus to the trigger element (if any)
      if (isOpen && menuTrigger.current) {
        (menuTrigger.current as HTMLElement).focus();
        menuTrigger.current = undefined;
      }
      // Toggle the menu
      return !isOpen;
    });
  };
  return (
    <React.Fragment>
      {WordpressHeadGlobal.Head()}
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(HOMEPAGE_SCHEMA) }}
        />
        {HERO_IMAGES.map((h, i) => (
          <link key={i} rel="preload" href={imgixUrl(h.url)} as="image" media={h.media} />
        ))}
      </Head>
      <WordpressHeadGlobal.Page>
        <GlobalStyles />
        <StickyCTAStyles />
        <StickyCTABar visible={stickyVisible} trackingTaxonomy="new_homepage" />
        <MenuPanel active={isMenuOpen} toggleMenuOpen={toggleMenu} />
        <MenuOverlay active={isMenuOpen} />
        <Header isMenuOpen={isMenuOpen} toggleMenuOpen={toggleMenu} />
        <main tabIndex={-1}>
          {isUnsupportedBrowser() && <UnsupportedBrowserBanner />}
          {!isUnsupportedBrowser() && returnExperienceBannerShown && (
            <ReturnExperienceBanner {...returnExperienceData} />
          )}
          <Hero
            // only show the sticky CTA when the hero isn't shown.
            isVisible={(isHeroVisible) => setStickyVisible(!isHeroVisible)}
            returnExperienceBannerShown={returnExperienceBannerShown}
          />
          <Press />
          <HowItWorks />
          <LazyLoad
            once
            offset={300}
            height={1000}
            placeholder={
              <Flex justifyContent="center" mt={50}>
                <Loader />
              </Flex>
            }
          >
            <SocialProofOdometer />
            <Testimonials />
            <SellingOptions />
            <BuyerPanel />
            <FAQs />
            <Contact markets={props.markets} campaignName="buy-sell-v2" />
            <AppBanner />
          </LazyLoad>
        </main>
        <LazyLoad once offset={300}>
          <Footer />
        </LazyLoad>
      </WordpressHeadGlobal.Page>
    </React.Fragment>
  );
};

Homepage2019.theme = eero;

const fetchSellerInputUUID = async (customerUUID: string) => {
  let sellerInputUUID;
  let hasMultipleProperties = false;

  // intentionally leaving this commented out for now, we want to bring back the cookie-based
  // return experience banner for some partnership leads

  // if exists fetch seller input uuid from cookie
  // sellerInputUUID = Cookies.get('lead_token');
  // if (sellerInputUUID) {
  //   if (!validate(sellerInputUUID)) {
  //     Cookies.remove('lead_token');
  //   } else {
  //     hasMultipleProperties = Cookies.get('has_multiple_properties') === 'true';
  //     return { sellerInputUUID, hasMultipleProperties };
  //   }
  // }

  // fetch addresses associated to customer
  const addresses_response = await api.fetchAdressesByCustomerUUID(customerUUID);
  if (!addresses_response.errors && addresses_response.data.sellDirect.getAddressesByCustomerUUID) {
    const addresses = addresses_response.data.sellDirect.getAddressesByCustomerUUID.addressToken;
    if (addresses.length > 0) {
      // fetch seller input uuid associated to customer and address
      const lead_response = await api.getLatestLeadQuery(customerUUID, addresses[0]);

      if (!lead_response.errors && lead_response.data.seller.getLatestLead) {
        sellerInputUUID = lead_response.data.seller.getLatestLead.lead.source.id;

        if (addresses.length > 1) {
          hasMultipleProperties = true;
        }
      }
    }
  }

  return { sellerInputUUID, hasMultipleProperties };
};

const fetchSellerReturnExperience = async (customerUUID: string) => {
  const { sellerInputUUID, hasMultipleProperties } = await fetchSellerInputUUID(customerUUID);
  if (!sellerInputUUID) {
    return;
  }

  const response = await api.fetchSellerReturnExperience(sellerInputUUID);
  if (!response || !response.data || response.errors) {
    return;
  }
  return {
    returnExperienceData: getReturnExperienceData(
      response.data,
      sellerInputUUID,
      hasMultipleProperties,
    ),
  };
};

const getReturnExperienceData = (
  data: any,
  sellerInputUUID: string,
  hasMultipleProperties: boolean,
): ReturnExperienceData => {
  if (!data.web && !data.reception) {
    return {
      experienceType: null,
    };
  }

  // If the lead is denied for all products then there is no return experience for them.
  const productOfferings: Array<any> = data.reception.getSellerInput.sellerInput.productOfferings;
  const allProductsDenied = productOfferings.every((po) => po.deniedAt);
  if (allProductsDenied) {
    return {
      experienceType: null,
    };
  }

  // Without an email the lead could not have qualified and thus we want to show the incomplete experience at this stage
  if (!data.reception.getSellerInput.sellerInput.answers.email) {
    return {
      sellerInputUUID: sellerInputUUID,
      address: data.reception.getSellerInput.sellerInput.address.street,
      experienceType: ExperienceType.incomplete_flow,
    };
  }

  const webLead = data.web.lead;
  // At this point we have an email which means the lead went through qualification process in web.
  // The endpoint we are using is the offer endpoint in web (for speed purposes). If there is no offer it returns no lead even if a lead exists
  // Thus no lead means there is no offer and hence the lead was denied. In this case we have no return experience to show
  if (!webLead) {
    return {
      experienceType: null,
    };
  }

  const address = webLead.address.street;
  const addressToken = webLead.address.address_token;
  const customerUUID = webLead.customer.id;
  const firstName = webLead.customer.firstName;
  const offerRequestSource = webLead.offer.offer_request_source;
  const currentOfferHeadlinePriceCents = webLead.offer.recorded_price_cents;
  const previousOfferHeadlinePriceCents = webLead.previousOffer.headlinePrice;
  const offerState = webLead.offer.state;
  const offerExpiresAt = webLead.offer.assessment_scheduling_expires_at;
  const isQualifiedLead = data?.sellDirect?.getLead?.lead?.isQualified;

  if (hasMultipleProperties) {
    return {
      customerUUID,
      firstName,
      experienceType: ExperienceType.multiple_properties,
    };
  }

  let offerExpiresInDays = undefined;
  if (offerExpiresAt) {
    const currentTime = new Date().getTime();
    const expirationTime = Date.parse(offerExpiresAt);
    // convert the milliseconds time difference into days and round up to nearest number of days
    offerExpiresInDays = Math.ceil((expirationTime - currentTime) / (1000 * 3600 * 24));
  }

  let experienceType = null;

  if (offerState == 'expired' && isQualifiedLead) {
    // https://github.com/opendoor-labs/code/blob/f6fb69d0fb84941597fb8084c470f9eac8aed67d/js/packages/cosmos/src/components/shared/ReturnExperienceBanner.tsx#L306 -> we show the refresh offer button if the experience type is "offer_expired"
    // let's only set the experience type to be "offer_expired" when we know that this lead is eligible for offer refresh (qualified for opendoor direct)
    experienceType = ExperienceType.offer_expired;
  } else if (
    offerState == 'sent' &&
    offerExpiresInDays &&
    offerExpiresInDays > 0 &&
    offerExpiresInDays <= OFFER_EXPIRY_DAYS_CUTOFF
  ) {
    experienceType = ExperienceType.offer_expiring;
  } else if (
    offerState == 'sent' &&
    currentOfferHeadlinePriceCents &&
    previousOfferHeadlinePriceCents &&
    currentOfferHeadlinePriceCents - previousOfferHeadlinePriceCents >= 1000
  ) {
    experienceType = ExperienceType.offer_increased;
  } else if (
    offerState == 'sent' &&
    currentOfferHeadlinePriceCents &&
    previousOfferHeadlinePriceCents &&
    currentOfferHeadlinePriceCents < previousOfferHeadlinePriceCents
  ) {
    experienceType = ExperienceType.offer_decreased;
  } else if (offerState == 'sent') {
    experienceType = ExperienceType.active_offer;
  }

  return {
    sellerInputUUID,
    address,
    addressToken,
    customerUUID,
    firstName,
    offerRequestSource,
    currentOfferHeadlinePriceCents,
    previousOfferHeadlinePriceCents,
    offerExpiresInDays,
    experienceType,
  };
};

const HomePage2019 = (props: ServerSideProps) => {
  return <Homepage2019 region={props.region} markets={props.markets} />;
};

const Page: NextPage<ServerSideProps> = (props) => <HomePage2019 {...props} />;

function getSingleValue(query: ParsedUrlQuery, key: string): string | undefined {
  const val = query[key] as string | string[] | undefined;
  // bail-out if the key doesn't exist. querystring types don't provide for this
  if (!val) {
    return;
  }
  // bail-out if it's an array, we didn't expect multiple values here
  if (Array.isArray(val)) {
    return;
  }
  return val;
}

export async function getServerSideProps(context: { req: any; query: ParsedUrlQuery }) {
  const markets = await api.fetchActiveMarkets(context.req.requestCache);

  let region = null;
  if (context.req) {
    region = getSingleValue(context.query, 'region') || null;
  }
  return {
    props: {
      region,
      markets,
    },
  }; // will be passed to the page component as props
}

export default Page;
